import React from 'react';
import { Box, InputAdornment, TextField } from '@mui/material';
import FieldError from './FieldError';

// ============================================================
// CommonTextField
//
// Reusable standard-variant text field used across the edit form.
//   - Gray 12px floating label (always shrunk) with optional red asterisk
//   - 16px black value
//   - Optional leading icon (rendered in brand blue by the caller)
//   - Inline red error text + icon below the field
//
// Controlled component: pass `value` + `onChange(nextValue)`.
// ============================================================

const labelSx = {
  color: '#6D6D6F',
  fontSize: '12px',
  lineHeight: '16px',
  letterSpacing: 0,
};

const inputSx = {
  color: '#000000',
  fontSize: '16px',
  lineHeight: '20px',
  letterSpacing: 0,
  fontWeight: 400,
  py: '6px',
};

const CommonTextField = ({
  label,
  value = '',
  onChange,
  onBlur,
  startIcon,
  error,
  errorMessage,
  required = false,
  disabled = false,
  placeholder,
  type = 'text',
  inputProps,
  helperText,
  percentageFlag= false
}) => {
  const hasError = Boolean(error || errorMessage);
  const [focused, setFocused] = React.useState(false);

  // Treat only null/undefined/'' as empty so that a value of 0 is preserved.
  const isEmpty = value === null || value === 0 || value === undefined || value === '';
  // Show the raw value while typing so entering/editing 0 works cleanly,
  // and append "%" inline only when the field is not focused.
  const displayValue = percentageFlag
    ? (isEmpty ? '' : focused ? value : `${value}%`)
    : value;

  return (
    <Box sx={{ width: '100%' }}>
      <TextField
        variant="standard"
        fullWidth
        required={required}
        disabled={disabled}
        type={type}
        value={displayValue}
        placeholder={placeholder}
        onChange={(e) => onChange?.(percentageFlag ? e.target.value.replace('%', '') : e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={(e) => {
          setFocused(false);
          onBlur?.(e);
        }}
        label={label}
        error={hasError}
        helperText={!hasError ? helperText : undefined}
        InputLabelProps={{ shrink: true, sx: labelSx }}
        InputProps={{
          startAdornment: startIcon ? (
            <InputAdornment position="start" sx={{ color: '#1460AA' }}>
              {startIcon}
            </InputAdornment>
          ) : undefined,
        }}
        inputProps={{
          'aria-invalid': hasError ? true : undefined,
          ...inputProps,
        }}
        sx={{
          '& .MuiInput-input': inputSx,
          '& .MuiFormLabel-asterisk': { color: '#D32F2F' },
          '& .MuiInput-underline:before': {
            borderBottomColor: '#C4C4C4',
          },
          '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
            borderBottomColor: '#1460AA',
          },
          '& .MuiInput-underline:after': {
            borderBottomColor: hasError ? '#9E231B' : '#1460AA',
          },
          ...(hasError && {
            '& .MuiInput-underline:before': { borderBottomColor: '#9E231B' },
          }),
        }}
      />
      {hasError && errorMessage && <FieldError message={errorMessage} />}
    </Box>
  );
};

export default CommonTextField;
